import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import Sidebar from "../components/Sidebar";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import {
  Brain, Upload, FileText, Search, Plus, Trash2, Check, X,
  Loader2, BookOpen, Target, Lightbulb, ArrowRight, RefreshCw,
  Download, CheckCircle, AlertCircle, ChevronRight, Eye, Save,
  HelpCircle, Sparkles, FileSearch, Database, Link2, GraduationCap, Building2
} from "lucide-react";
import { ModelosAseguradoras } from "./ModelosAseguradoras";

export default function AprendizajePage() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("analizar");
  
  // Campos del sistema
  const [camposSistema, setCamposSistema] = useState([]);
  const [categorias, setCategorias] = useState({});
  
  // Diccionario
  const [diccionario, setDiccionario] = useState([]);
  const [searchDiccionario, setSearchDiccionario] = useState("");
  
  // Análisis de documento
  const [uploading, setUploading] = useState(false);
  const [analisisResultado, setAnalisisResultado] = useState(null);
  const [terminosSeleccionados, setTerminosSeleccionados] = useState({});
  const [guardandoMapeos, setGuardandoMapeos] = useState(false);
  
  // Agregar manual
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [nuevoTermino, setNuevoTermino] = useState("");
  const [campoPadreSeleccionado, setCampoPadreSeleccionado] = useState("");
  
  // Estadísticas
  const [estadisticas, setEstadisticas] = useState(null);
  
  // Historial
  const [documentosAnalizados, setDocumentosAnalizados] = useState([]);

  // Cargar datos iniciales
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [camposRes, diccionarioRes, estadisticasRes, documentosRes] = await Promise.all([
        axios.get(`${API}/aprendizaje/campos-sistema`, getAuthHeaders()),
        axios.get(`${API}/aprendizaje/diccionario`, getAuthHeaders()),
        axios.get(`${API}/aprendizaje/estadisticas`, getAuthHeaders()),
        axios.get(`${API}/aprendizaje/documentos`, getAuthHeaders())
      ]);
      
      setCamposSistema(camposRes.data.campos || []);
      setCategorias(camposRes.data.categorias || {});
      setDiccionario(diccionarioRes.data.diccionario || []);
      setEstadisticas(estadisticasRes.data);
      setDocumentosAnalizados(documentosRes.data || []);
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Subir y analizar documento
  const handleUploadDocument = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setAnalisisResultado(null);
    setTerminosSeleccionados({});
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/aprendizaje/analizar-documento`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (res.data.success) {
        setAnalisisResultado(res.data);
        // Preseleccionar términos con alta confianza
        const preseleccion = {};
        (res.data.terminos_encontrados || []).forEach((t, idx) => {
          if (t.confianza === "alta") {
            preseleccion[idx] = true;
          }
        });
        setTerminosSeleccionados(preseleccion);
        toast.success(`Documento analizado: ${res.data.terminos_encontrados?.length || 0} términos encontrados`);
      } else {
        toast.error(res.data.error || "Error al analizar documento");
      }
    } catch (err) {
      console.error("Error uploading:", err);
      toast.error(err.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
    }
  };

  // Dropzone para arrastrar y soltar archivos
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      handleUploadDocument(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'text/html': ['.html']
    },
    maxFiles: 1,
    disabled: uploading
  });

  // Guardar mapeos seleccionados
  const handleGuardarMapeos = async () => {
    const terminos = analisisResultado?.terminos_encontrados || [];
    const mapeos = [];
    
    Object.keys(terminosSeleccionados).forEach(idx => {
      if (terminosSeleccionados[idx]) {
        const t = terminos[parseInt(idx)];
        if (t) {
          mapeos.push({
            termino: t.termino,
            campo_sistema_id: t.campo_sugerido_id,
            campo_sistema_label: t.campo_sugerido_label,
            confianza: t.confianza === "alta" ? 100 : t.confianza === "media" ? 70 : 40
          });
        }
      }
    });
    
    if (mapeos.length === 0) {
      toast.warning("Selecciona al menos un término para guardar");
      return;
    }
    
    setGuardandoMapeos(true);
    try {
      const res = await axios.post(`${API}/aprendizaje/guardar-mapeos`, {
        mapeos,
        documento_origen: analisisResultado?.filename,
        from_ai: true
      }, getAuthHeaders());
      
      toast.success(res.data.mensaje);
      setAnalisisResultado(null);
      setTerminosSeleccionados({});
      fetchData();
    } catch (err) {
      toast.error("Error al guardar mapeos");
    } finally {
      setGuardandoMapeos(false);
    }
  };

  // Agregar sinónimo manual
  const handleAgregarManual = async () => {
    if (!nuevoTermino.trim() || !campoPadreSeleccionado) {
      toast.warning("Completa todos los campos");
      return;
    }
    
    try {
      const campo = camposSistema.find(c => c.id === campoPadreSeleccionado);
      await axios.post(`${API}/aprendizaje/agregar-sinonimo`, {
        termino: nuevoTermino,
        campo_sistema_id: campoPadreSeleccionado,
        campo_sistema_label: campo?.label || ""
      }, getAuthHeaders());
      
      toast.success("Sinónimo agregado");
      setShowAddDialog(false);
      setNuevoTermino("");
      setCampoPadreSeleccionado("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al agregar sinónimo");
    }
  };

  // Cargar sinónimos predefinidos
  const [cargandoPredefinidos, setCargandoPredefinidos] = useState(false);
  
  const handleCargarPredefinidos = async () => {
    if (!window.confirm("¿Cargar los sinónimos predefinidos para aseguradoras de República Dominicana? Esto agregará aproximadamente 130 términos comunes.")) return;
    
    setCargandoPredefinidos(true);
    try {
      const res = await axios.post(`${API}/aprendizaje/cargar-predefinidos`, {}, getAuthHeaders());
      toast.success(res.data.mensaje);
      fetchData();
    } catch (err) {
      toast.error("Error al cargar sinónimos predefinidos");
    } finally {
      setCargandoPredefinidos(false);
    }
  };

  // Exportar a Excel
  const handleExportarExcel = async () => {
    try {
      const response = await axios.get(`${API}/aprendizaje/exportar-excel`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Crear link de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diccionario_sinonimos_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Diccionario exportado correctamente");
    } catch (err) {
      toast.error("Error al exportar diccionario");
    }
  };

  // Importar desde Excel
  const [importando, setImportando] = useState(false);
  
  const handleImportarExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportando(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/aprendizaje/importar-excel`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          "Content-Type": "multipart/form-data"
        }
      });
      
      if (res.data.success) {
        toast.success(res.data.mensaje);
        if (res.data.errores && res.data.errores.length > 0) {
          console.warn("Errores de importación:", res.data.errores);
        }
        fetchData();
      } else {
        toast.error(res.data.error || "Error al importar");
      }
    } catch (err) {
      toast.error("Error al importar archivo");
    } finally {
      setImportando(false);
      e.target.value = "";
    }
  };

  // Eliminar sinónimo
  const handleEliminarSinonimo = async (sinonimoId) => {
    if (!window.confirm("¿Eliminar este sinónimo del diccionario?")) return;
    
    try {
      await axios.delete(`${API}/aprendizaje/sinonimo/${sinonimoId}`, getAuthHeaders());
      toast.success("Sinónimo eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Toggle selección de término
  const toggleTermino = (idx) => {
    setTerminosSeleccionados(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  // Seleccionar/deseleccionar todos
  const toggleTodos = (seleccionar) => {
    const terminos = analisisResultado?.terminos_encontrados || [];
    const nuevaSeleccion = {};
    terminos.forEach((_, idx) => {
      nuevaSeleccion[idx] = seleccionar;
    });
    setTerminosSeleccionados(nuevaSeleccion);
  };

  // Filtrar diccionario
  const diccionarioFiltrado = diccionario.filter(d =>
    d.campo_label?.toLowerCase().includes(searchDiccionario.toLowerCase()) ||
    d.sinonimos?.some(s => s.termino?.toLowerCase().includes(searchDiccionario.toLowerCase()))
  );

  // Badge de confianza
  const ConfianzaBadge = ({ confianza }) => {
    const config = {
      alta: { class: "bg-green-100 text-green-700 border-green-300", label: "Alta" },
      media: { class: "bg-amber-100 text-amber-700 border-amber-300", label: "Media" },
      baja: { class: "bg-red-100 text-red-700 border-red-300", label: "Baja" }
    };
    const c = config[confianza] || config.baja;
    return <Badge variant="outline" className={c.class}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="aprendizaje-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Brain className="w-8 h-8 text-purple-500" />
              Aprendizaje IA
            </h1>
            <p className="text-slate-500 mt-1">
              Entrena al sistema para reconocer términos de diferentes aseguradoras
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={handleCargarPredefinidos}
              disabled={cargandoPredefinidos}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {cargandoPredefinidos ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Predefinidos RD
            </Button>
            <Button 
              variant="outline" 
              onClick={handleExportarExcel}
              className="border-green-300 text-green-700 hover:bg-green-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar Excel
            </Button>
            <div className="relative">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportarExcel}
                className="hidden"
                id="import-excel"
                disabled={importando}
              />
              <label htmlFor="import-excel">
                <Button 
                  variant="outline" 
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 cursor-pointer"
                  disabled={importando}
                  asChild
                >
                  <span>
                    {importando ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Importar Excel
                  </span>
                </Button>
              </label>
            </div>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button onClick={() => setShowAddDialog(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Sinónimo
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Database className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{estadisticas?.total_sinonimos || 0}</p>
                  <p className="text-sm text-slate-500">Sinónimos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{estadisticas?.total_documentos || 0}</p>
                  <p className="text-sm text-slate-500">Documentos Analizados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{camposSistema.length}</p>
                  <p className="text-sm text-slate-500">Campos del Sistema</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 dark:bg-amber-900 rounded-lg">
                  <Sparkles className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{estadisticas?.por_origen?.ia || 0}</p>
                  <p className="text-sm text-slate-500">Aprendidos por IA</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="analizar" className="gap-2">
              <FileSearch className="w-4 h-4" />
              Analizar Documento
            </TabsTrigger>
            <TabsTrigger value="diccionario" className="gap-2">
              <BookOpen className="w-4 h-4" />
              Diccionario
            </TabsTrigger>
            <TabsTrigger value="modelos" className="gap-2" data-testid="tab-modelos">
              <Building2 className="w-4 h-4" />
              Modelos de Aseguradoras
            </TabsTrigger>
            <TabsTrigger value="campos" className="gap-2">
              <Database className="w-4 h-4" />
              Campos del Sistema
            </TabsTrigger>
            <TabsTrigger value="historial" className="gap-2">
              <FileText className="w-4 h-4" />
              Historial
            </TabsTrigger>
          </TabsList>

          {/* Tab: Analizar Documento */}
          <TabsContent value="analizar">
            <div className="grid grid-cols-2 gap-6">
              {/* Upload Area */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5 text-purple-500" />
                    Cargar Documento
                  </CardTitle>
                  <CardDescription>
                    Sube un documento de póliza para que la IA extraiga los términos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    {...getRootProps()} 
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
                      isDragActive 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                        : 'border-slate-300 dark:border-slate-600 hover:border-purple-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                    } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <input {...getInputProps()} />
                    {uploading ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
                        <p className="text-slate-600 dark:text-slate-400">Analizando documento...</p>
                        <p className="text-sm text-slate-400">La IA está leyendo el contenido con OCR</p>
                      </div>
                    ) : isDragActive ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-4 bg-purple-200 dark:bg-purple-800 rounded-full animate-pulse">
                          <Upload className="w-8 h-8 text-purple-600 dark:text-purple-300" />
                        </div>
                        <p className="text-purple-600 dark:text-purple-300 font-medium">
                          Suelta el archivo aquí...
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-4 bg-purple-100 dark:bg-purple-900 rounded-full">
                          <FileSearch className="w-8 h-8 text-purple-600" />
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 font-medium">
                          Arrastra un archivo aquí o haz clic para seleccionar
                        </p>
                        <p className="text-sm text-slate-400">
                          PDF, Word, Excel, TXT, HTML
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">Drag & Drop</Badge>
                          <Badge variant="outline" className="text-xs">OCR Incluido</Badge>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="font-medium flex items-center gap-2 text-blue-700 dark:text-blue-300">
                      <Lightbulb className="w-4 h-4" />
                      ¿Cómo funciona?
                    </h4>
                    <ol className="mt-2 text-sm text-blue-600 dark:text-blue-400 space-y-1 list-decimal list-inside">
                      <li>Sube un documento de una aseguradora (póliza, cotización, etc.)</li>
                      <li>La IA lee y extrae todos los términos encontrados</li>
                      <li>Sugiere qué campo del sistema corresponde a cada término</li>
                      <li>Tú confirmas los mapeos correctos y se guardan</li>
                      <li>En futuras comparaciones, el sistema reconocerá estos términos</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>

              {/* Resultados del análisis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Términos Encontrados
                  </CardTitle>
                  <CardDescription>
                    {analisisResultado 
                      ? `${analisisResultado.terminos_encontrados?.length || 0} términos detectados en "${analisisResultado.filename}"`
                      : "Los términos aparecerán aquí después de analizar un documento"
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!analisisResultado ? (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Sube un documento para comenzar</p>
                    </div>
                  ) : (
                    <>
                      {/* Info del documento */}
                      {analisisResultado.aseguradora_detectada && (
                        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-green-700 dark:text-green-300">
                            Aseguradora detectada: <strong>{analisisResultado.aseguradora_detectada}</strong>
                          </span>
                        </div>
                      )}

                      {/* Acciones */}
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleTodos(true)}>
                            Seleccionar todos
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => toggleTodos(false)}>
                            Deseleccionar
                          </Button>
                        </div>
                        <Badge variant="outline">
                          {Object.values(terminosSeleccionados).filter(Boolean).length} seleccionados
                        </Badge>
                      </div>

                      {/* Lista de términos */}
                      <ScrollArea className="h-[350px]">
                        <div className="space-y-2">
                          {(analisisResultado.terminos_encontrados || []).map((termino, idx) => (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                                terminosSeleccionados[idx]
                                  ? "bg-purple-50 dark:bg-purple-900/20 border-purple-300"
                                  : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-50"
                              }`}
                              onClick={() => toggleTermino(idx)}
                            >
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={terminosSeleccionados[idx] || false}
                                  onCheckedChange={() => toggleTermino(idx)}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-slate-800 dark:text-slate-200">
                                      "{termino.termino}"
                                    </span>
                                    <ArrowRight className="w-4 h-4 text-slate-400" />
                                    <Badge className="bg-blue-100 text-blue-700">
                                      {termino.campo_sugerido_label}
                                    </Badge>
                                    <ConfianzaBadge confianza={termino.confianza} />
                                  </div>
                                  {termino.contexto && (
                                    <p className="text-xs text-slate-500 truncate">
                                      Contexto: {termino.contexto}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>

                      {/* Botón guardar */}
                      <div className="mt-4 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setAnalisisResultado(null)}>
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleGuardarMapeos}
                          disabled={guardandoMapeos || Object.values(terminosSeleccionados).filter(Boolean).length === 0}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {guardandoMapeos ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Guardar Mapeos
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Tab: Diccionario */}
          {/* Tab: Modelos de Aseguradoras */}
          <TabsContent value="modelos">
            <ModelosAseguradoras />
          </TabsContent>

          {/* Tab: Diccionario */}
          <TabsContent value="diccionario">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-green-500" />
                      Diccionario de Sinónimos
                    </CardTitle>
                    <CardDescription>
                      Términos aprendidos y su mapeo a campos del sistema
                    </CardDescription>
                  </div>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar..."
                      value={searchDiccionario}
                      onChange={e => setSearchDiccionario(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {diccionarioFiltrado.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay sinónimos en el diccionario</p>
                    <p className="text-sm mt-1">Analiza documentos para empezar a entrenar el sistema</p>
                  </div>
                ) : (
                  <Accordion type="multiple" className="space-y-2">
                    {diccionarioFiltrado.map((campo) => (
                      <AccordionItem key={campo.campo_id} value={campo.campo_id} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <Link2 className="w-5 h-5 text-blue-500" />
                            <span className="font-medium">{campo.campo_label}</span>
                            <Badge variant="outline" className="ml-2">
                              {campo.sinonimos?.length || 0} sinónimos
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pt-2 pb-4">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Término</TableHead>
                                  <TableHead>Confianza</TableHead>
                                  <TableHead>Origen</TableHead>
                                  <TableHead>Documento</TableHead>
                                  <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {campo.sinonimos?.map(sin => (
                                  <TableRow key={sin.id}>
                                    <TableCell className="font-medium">"{sin.termino}"</TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className={
                                        sin.confianza >= 80 ? "bg-green-50 text-green-700" :
                                        sin.confianza >= 50 ? "bg-amber-50 text-amber-700" :
                                        "bg-red-50 text-red-700"
                                      }>
                                        {sin.confianza}%
                                      </Badge>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={sin.origen === "ia" ? "default" : "secondary"}>
                                        {sin.origen === "ia" ? "IA" : "Manual"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-slate-500 text-sm">
                                      {sin.documento_origen || "-"}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleEliminarSinonimo(sin.id)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Campos del Sistema */}
          <TabsContent value="campos">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-blue-500" />
                  Campos del Sistema para Pólizas
                </CardTitle>
                <CardDescription>
                  Estos son los campos estándar a los que se mapean los términos de las aseguradoras
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  {Object.entries(categorias).map(([categoria, campos]) => (
                    <Card key={categoria} className="border-slate-200 dark:border-slate-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base capitalize flex items-center gap-2">
                          {categoria === "cliente" && <Target className="w-4 h-4 text-blue-500" />}
                          {categoria === "poliza" && <FileText className="w-4 h-4 text-green-500" />}
                          {categoria === "costos" && <Database className="w-4 h-4 text-amber-500" />}
                          {categoria === "cobertura" && <CheckCircle className="w-4 h-4 text-purple-500" />}
                          {categoria === "beneficiario" && <Target className="w-4 h-4 text-pink-500" />}
                          {categoria === "intermediario" && <GraduationCap className="w-4 h-4 text-indigo-500" />}
                          {categoria === "pago" && <Database className="w-4 h-4 text-teal-500" />}
                          {categoria.charAt(0).toUpperCase() + categoria.slice(1)}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-2">
                          {campos.map(campo => {
                            const sinonimosCount = diccionario.find(d => d.campo_id === campo.id)?.sinonimos?.length || 0;
                            return (
                              <div key={campo.id} className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-800">
                                <div>
                                  <p className="font-medium text-sm">{campo.label}</p>
                                  <p className="text-xs text-slate-500">{campo.descripcion}</p>
                                </div>
                                {sinonimosCount > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {sinonimosCount} sinónimos
                                  </Badge>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Historial */}
          <TabsContent value="historial">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-500" />
                  Documentos Analizados
                </CardTitle>
                <CardDescription>
                  Historial de documentos que has subido para entrenar el sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                {documentosAnalizados.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay documentos analizados</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Archivo</TableHead>
                        <TableHead>Tipo Detectado</TableHead>
                        <TableHead>Aseguradora</TableHead>
                        <TableHead>Términos</TableHead>
                        <TableHead>Fecha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documentosAnalizados.map(doc => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-medium">{doc.filename}</TableCell>
                          <TableCell>{doc.resultado?.documento_tipo || "-"}</TableCell>
                          <TableCell>{doc.resultado?.aseguradora_detectada || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {doc.resultado?.terminos_encontrados?.length || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {new Date(doc.created_at).toLocaleDateString("es-ES")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog: Agregar sinónimo manual */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-purple-500" />
                Agregar Sinónimo Manual
              </DialogTitle>
              <DialogDescription>
                Agrega un término que se usará para identificar un campo del sistema
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Término / Sinónimo</Label>
                <Input
                  value={nuevoTermino}
                  onChange={e => setNuevoTermino(e.target.value)}
                  placeholder="Ej: Beneficiario, Asegurado, Titular..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  El término como aparece en los documentos de las aseguradoras
                </p>
              </div>
              <div>
                <Label>Campo del Sistema</Label>
                <Select value={campoPadreSeleccionado} onValueChange={setCampoPadreSeleccionado}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el campo correspondiente" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categorias).map(([cat, campos]) => (
                      <div key={cat}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">
                          {cat}
                        </div>
                        {campos.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAgregarManual} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Agregar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
